import { IconButton, List, Shimmer, ShimmerElementType, TooltipHost } from "@fluentui/react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Patient } from "../../api/patients.types";
import { getPodById, getPodsByProviderId } from "../../api/pods";
import { Pod, PodProvider } from "../../api/pods.types";
import { ROUTES } from "../../config/routes";
import PodPersona from "./PodPersona";
import PodPlaceholder from "./PodPlaceholder";
import styles from './CareTeam.module.scss';
import { coalesceAndSortProviders, getProviderCategoryByPosition } from "./Util";

interface Props {
  patient?: Patient
}

const getPatientPod = async (patient: Patient): Promise<Pod | undefined> => {
  const providerId = patient.preferredDoctor?.doctor.providerId;
  
  if (!providerId) {
    return undefined;
  }

  const podResults = await getPodsByProviderId(providerId);

  if (podResults.length === 0) {
    return undefined;
  }

  const podData = await getPodById(podResults[0].id);

  if (!podData) {
    return undefined;
  }

  return podData;
}

const CareTeam = ({patient}: Props) => {
  const [podData, setPodData] = useState<Pod>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [providerList, setProviderList] = useState<PodProvider[]>();

  /**
   * Sort the providers into the same order as the pod detail view, ensuring that
   * the physician comes first, then the clinicians, and finally the support staff.
   * Because we're using coalesceAndSortProviders upfront, each bin of providers
   * will be in alphabetical order, so the destructured result here will match
   * what the pod detail view would look like if the group headers were invisible.
   */
  const listProviders = async (pod: Pod) => {
    const sortedProviders = (await coalesceAndSortProviders(pod)).providers;
    const bins: Record<Exclude<ReturnType<typeof getProviderCategoryByPosition>, undefined>, PodProvider[]> = { "Physician": [], "Clinician": [], "Support Staff": [] };

    for (let provider of sortedProviders) {
      if (provider.status !== 'ACTIVATED') continue;

      const category = getProviderCategoryByPosition(provider);
      if (!category) continue;

      bins[category].push(provider);
    }

    setProviderList([...bins.Physician, ...bins.Clinician, ...bins["Support Staff"]]);
    setIsLoading(false);
  };

  useEffect(() => {
    if (patient) {
      setIsLoading(true);
      setError('');

      getPatientPod(patient)
        .then(pod => {
          setPodData(pod);
          
          if (!pod) {
            setProviderList([]);
            setIsLoading(false);
          } else {
            listProviders(pod);
          }
        })
        .catch(error => {
          setPodData(undefined);
          setError('Unable to load pod details.');
          setIsLoading(false);
        });
    }
  }, [patient]);

  if (isLoading) {
    return <CareTeamShimmer />;
  }

  if (error !== '') {
    return (
      <div className={styles.container}>
        <PodPlaceholder full type="error" iconName="Cancel" text={error} />
      </div>
    );
  }

  if (!podData) {
    return (
      <div className={styles.container}>
        <PodPlaceholder full iconName="WebAppBuilderModule" text="This patient is not assigned to a pod." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.titleWrapper}>
        <h1 className={styles.title}>{podData.name}</h1>
        <TooltipHost content="View pod">
          <Link to={`${ROUTES.pods}/${podData.id}`} tabIndex={-1} data-is-focusable={false}>
            <IconButton
              iconProps={{ iconName: "NavigateExternalInline" }}
              ariaLabel="View pod"
            />
          </Link>
        </TooltipHost>
      </div>
      <div className={styles.fields}>
        <List
          className={styles.providerList}
          items={providerList}
          onRenderCell={(item) => {
            if (!item) return null;

            return (
              <div className={styles.personaWrapper}>
                <PodPersona provider={item} className={styles.persona} secondaryText={item.position} />
                <TooltipHost content="View profile">
                  <Link to={`${ROUTES.providers}/${item.providerId}`} tabIndex={-1} data-is-focusable={false}>
                    <IconButton
                      iconProps={{ iconName: "ContactCard" }}
                      ariaLabel="View profile"
                      className={styles.viewButton}
                    />
                  </Link>
                </TooltipHost>
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}

export default CareTeam;

const CareTeamShimmer = () => {
  return (
    <div className={styles.container}>
      <div className={styles.titleWrapper}>
        <h1 className={styles.title}>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 27 }, { type: ShimmerElementType.gap, width: 32 }]}/>
        </h1>
        <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 32, width: 32 }]}/>
      </div>
      <div className={styles.fields}>
        <div>
          <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 175, width: 450 }]}/>
        </div>
      </div>
    </div>
  );
};